import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SliderFilterConfig } from '@alltrails/denali/components/FilterBar';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

const ratings = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 4.6, 4.7, 4.8, 4.9, 5];

export const isValidRating = (rating: number) => ratings.includes(rating);

const useMinRatingFilterConfig = (
  exploreServiceFilters: ExploreServiceFilters,
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void,
  formatHeaderValue: (rating: number) => ReactNode
): SliderFilterConfig => {
  const intl = useIntl();

  const minRating = exploreServiceFilters.minRating || 0;

  const marks = useMemo(
    () =>
      ratings.map(rating => {
        return {
          value: rating
        };
      }),
    []
  );

  const onChange = useCallback(
    (value: number) => {
      const newFilters = { ...exploreServiceFilters };
      delete newFilters.minRating;
      if (value === 0) {
        // Do nothing, act like there is no rating filter
      } else {
        newFilters.minRating = value;
      }
      onFiltersChange(newFilters);
    },
    [exploreServiceFilters, onFiltersChange]
  );

  const clear = useMemo(() => {
    if (minRating === 0) {
      return undefined;
    }
    return () => onChange(0);
  }, [minRating, onChange]);

  return {
    type: 'slider',
    id: 'minRating',
    filterChip: {
      onClear: clear,
      selectedItems: undefined, // We don't actually render this as a chip so no need to handle the star rendering atm
      testId: 'minRating-filter-chip',
      unselectedText: intl.formatMessage({ defaultMessage: 'Rating' })
    },
    slider: {
      formatHeaderValue,
      isReversed: true,
      label: intl.formatMessage({ defaultMessage: 'Rating' }),
      marks,
      onChange,
      testId: 'minRating-filter',
      value: minRating
    }
  };
};

export default useMinRatingFilterConfig;
